import { Button, Typography } from "@heroui/react";
import { Icon } from "@iconify/react";
import clsx from "clsx";
import { ReactNode, UIEvent, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSidebarLayoutStore } from "@/stores/sidebar-layout";

const HIDE_THRESHOLD = 80;
const UP_TOLERANCE = 6;
const DOWN_TOLERANCE = 10;

interface PageLayoutProps {
  // Optional: tab-level pages (e.g. channel manage) omit it because the tab
  // already names the section — they show only a description + CTA.
  title?: ReactNode;
  children: ReactNode;
  buttons?: Array<ReactNode>;
  description?: ReactNode;
  showBack?: boolean;
  onBackUrl?: string;
  onBackClick?: () => void;
  className?: string;
  containerClassName?: string;
  // "compact" renders a smaller, tightly-spaced header — used where the page
  // already sits under section chrome (e.g. the channel manage tab strip).
  variant?: "default" | "compact";
}

export const PageLayout = ({
  children,
  title,
  buttons,
  description,
  showBack,
  onBackUrl,
  onBackClick,
  className,
  containerClassName,
  variant = "default",
}: PageLayoutProps) => {
  const lastYRef = useRef(0);
  const tickingRef = useRef(false);

  const [isHidden, setIsHidden] = useState(false);

  const navigate = useNavigate();
  const { isSidebarOpen, toggleSidebar } = useSidebarLayoutStore();

  const isCompact = variant === "compact";

  const handleBack = useCallback(() => {
    if (onBackClick) {
      onBackClick();
    } else if (onBackUrl) {
      navigate(onBackUrl);
    } else {
      navigate(-1);
    }
  }, [navigate, onBackUrl, onBackClick]);

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (tickingRef.current) return;
    tickingRef.current = true;

    requestAnimationFrame(() => {
      const y = target.scrollTop;
      const delta = y - lastYRef.current;

      if (y < HIDE_THRESHOLD) {
        setIsHidden(false);
        lastYRef.current = y;
      } else if (delta > DOWN_TOLERANCE) {
        setIsHidden(true);
        lastYRef.current = y;
      } else if (delta < -UP_TOLERANCE) {
        setIsHidden(false);
        lastYRef.current = y;
      }

      tickingRef.current = false;
    });
  }, []);

  return (
    <div
      className={clsx(
        "flex flex-col w-full h-full relative bg-background overflow-auto",
        className,
      )}
      onScroll={handleScroll}
    >
      <header
        className={clsx(
          "sticky top-0 z-40 flex items-center gap-3 justify-between bg-background transition-transform duration-300 ease-out",
          isCompact ? "px-4 py-2" : "pt-4 py-2 px-4",
          isHidden && "-translate-y-full",
        )}
      >
        <div className="flex items-center gap-2 shrink-0">
          {!isSidebarOpen && (
            <Button
              isIconOnly
              size="sm"
              variant="tertiary"
              onPress={toggleSidebar}
            >
              <Icon fontSize={20} icon="solar:sidebar-minimalistic-linear" />
            </Button>
          )}
          {showBack && (
            <Icon
              className="cursor-pointer hover:text-muted p-1"
              fontSize={isCompact ? 26 : 34}
              icon={"solar:alt-arrow-left-linear"}
              onClick={handleBack}
            />
          )}
          {(title || description) && (
            <div className={clsx(isCompact && "leading-tight")}>
              {title && (
                <Typography type={isCompact ? "h6" : "h4"}>{title}</Typography>
              )}
              {description &&
                (typeof description === "string" ? (
                  <Typography
                    className={clsx(
                      isCompact && "leading-tight",
                      // Title-less compact header: the description is the primary
                      // line, so nudge it up a touch from body-sm.
                      isCompact && !title && "text-[15px]",
                    )}
                    color="muted"
                    type={isCompact && title ? "body-xs" : "body-sm"}
                  >
                    {description}
                  </Typography>
                ) : (
                  // Rich descriptions (e.g. a metadata row with chips) render
                  // as-is so they aren't forced into a single muted paragraph.
                  <div className="mt-0.5">{description}</div>
                ))}
            </div>
          )}
        </div>
        <div
          id="page-header-actions"
          className="flex items-center gap-2 w-fit ml-auto"
        >
          {buttons}
        </div>
      </header>

      <main
        className={clsx(
          "w-full flex-1",
          isCompact ? "px-4 pb-4 pt-1" : "p-4",
          containerClassName,
        )}
      >
        {children}
      </main>
    </div>
  );
};
